<template>
  <div class="revenue-analysis-page">
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title"><i class="el-icon-coin"></i> 营收分析</h2>
        <span class="page-desc">分析平台营收趋势、收入构成及资金流向</span>
      </div>
    </div>
    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-radio-group v-model="timeType" size="small" @change="loadData">
          <el-radio-button :label="0">今日</el-radio-button>
          <el-radio-button :label="1">昨日</el-radio-button>
          <el-radio-button :label="2">近7天</el-radio-button>
          <el-radio-button :label="3">近30天</el-radio-button>
          <el-radio-button :label="4">近半年</el-radio-button>
          <el-radio-button :label="5">近一年</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-right">
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData" :loading="loading">刷新数据</el-button>
      </div>
    </div>

    <!-- 概览卡片 -->
    <div class="section-title"><i class="el-icon-coin"></i> 营收概览</div>
    <div class="overview-cards">
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#67C23A,#85CE61)"><i class="el-icon-money"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">¥{{ formatMoney(stats.totalRevenue) }}</div><div class="ov-card-label">总营收</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#409EFF,#66B1FF)"><i class="el-icon-tickets"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">¥{{ formatMoney(stats.ticketRevenue) }}</div><div class="ov-card-label">票款收入</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#E6A23C,#EBB563)"><i class="el-icon-goods"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">¥{{ formatMoney(stats.serviceRevenue) }}</div><div class="ov-card-label">服务费收入</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#F56C6C,#F78989)"><i class="el-icon-refresh-left"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">¥{{ formatMoney(stats.refundAmount) }}</div><div class="ov-card-label">退款金额</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#909399,#A6A9AD)"><i class="el-icon-document"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ stats.orderCount }}</div><div class="ov-card-label">成交订单</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#9B59B6,#B07CC6)"><i class="el-icon-coin"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">¥{{ formatMoney(stats.avgOrderAmount) }}</div><div class="ov-card-label">客单价</div></div>
      </div>
    </div>

    <!-- 图表 3列 -->
    <div class="charts-grid">
      <div class="chart-card" style="grid-column:span 2">
        <div class="chart-card-header">
          <span><i class="el-icon-data-line"></i> 营收趋势</span>
          <el-radio-group v-model="trendTimeType" size="mini" @change="loadRevenueTrend">
            <el-radio-button :label="7">近7天</el-radio-button>
            <el-radio-button :label="30">近30天</el-radio-button>
            <el-radio-button :label="90">近3月</el-radio-button>
            <el-radio-button :label="180">近半年</el-radio-button>
            <el-radio-button :label="365">近一年</el-radio-button>
          </el-radio-group>
        </div>
        <div class="chart-body" ref="revenueTrendChart"></div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-pie-chart"></i> 收入构成</span></div>
        <div class="chart-body" ref="revenueComposeChart"></div>
      </div>
    </div>

    <!-- 图表 2列 -->
    <div class="charts-grid-half">
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-s-data"></i> 线路营收排行</span></div>
        <div class="chart-body" ref="lineRevenueChart"></div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-bank-card"></i> 支付方式分布</span></div>
        <div class="chart-body" ref="paymentChart"></div>
      </div>
    </div>

    <!-- 营收明细 -->
    <div class="table-card">
      <div class="chart-card-header">
        <span><i class="el-icon-document"></i> 营收明细</span>
        <el-button type="primary" size="mini" plain @click="exportData"><i class="el-icon-download"></i> 导出</el-button>
      </div>
      <div style="padding:16px">
        <el-table :data="revenueList" size="small" max-height="300" border>
          <el-table-column prop="organizationName" label="机构" min-width="100" show-overflow-tooltip></el-table-column>
          <el-table-column prop="date" label="日期" width="120"></el-table-column>
          <el-table-column prop="orderCount" label="订单数" width="100" align="right"></el-table-column>
          <el-table-column prop="passengerCount" label="乘客数" width="100" align="right"></el-table-column>
          <el-table-column label="票款收入" width="120" align="right">
            <template slot-scope="{ row }">¥{{ formatMoney(row.ticketAmount) }}</template>
          </el-table-column>
          <el-table-column label="服务费" width="100" align="right">
            <template slot-scope="{ row }">¥{{ formatMoney(row.serviceAmount) }}</template>
          </el-table-column>
          <el-table-column label="退款" width="100" align="right">
            <template slot-scope="{ row }"><span style="color:#F56C6C">-¥{{ formatMoney(row.refundAmount) }}</span></template>
          </el-table-column>
          <el-table-column label="实收金额" align="right">
            <template slot-scope="{ row }"><span style="font-weight:600;color:#67C23A">¥{{ formatMoney(row.actualAmount) }}</span></template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "RevenueAnalysis",
  data() {
    return {
      loading: false,
      timeType: 0,
      trendTimeType: 7,
      stats: {
        totalRevenue: 0,
        revenueTrend: 0,
        ticketRevenue: 0,
        serviceRevenue: 0,
        refundAmount: 0,
        orderCount: 0,
        avgOrderAmount: 0
      },
      revenueList: [],
      charts: {},
      resizeHandler: null
    };
  },
  mounted() {
    this.loadData();
    this.resizeHandler = () => Object.values(this.charts).forEach(c => c && c.resize());
    window.addEventListener("resize", this.resizeHandler);
  },
  beforeDestroy() {
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler);
    Object.values(this.charts).forEach(c => c && c.dispose());
  },
  methods: {
    formatMoney(num) {
      if (!num) return "0.00";
      return parseFloat(num).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    initChart(refName) {
      if (this.charts[refName]) this.charts[refName].dispose();
      const dom = this.$refs[refName];
      if (!dom) return null;
      const chart = echarts.init(dom);
      this.charts[refName] = chart;
      return chart;
    },
    async loadData() {
      this.loading = true;
      await Promise.all([
        this.loadStats(),
        this.loadRevenueTrend(),
        this.loadRevenueCompose(),
        this.loadLineRevenue(),
        this.loadPaymentDist(),
        this.loadRevenueList()
      ]);
      this.loading = false;
    },
    async loadStats() {
      try {
        const res = await this.$axios.get("/api/v1/manage/statistics/dataBriefing", { timeType: "2" });
        if (res && res.code === 1) {
          const data = res.data || {};
          const orderPayAmount = data.orderPayAmount || 0;
          const orderFinishNum = data.orderFinishNum || 0;
          this.stats = {
            totalRevenue: orderPayAmount,
            revenueTrend: data.revenueTrend || 0,
            ticketRevenue: data.ticketAmount || 0,
            serviceRevenue: data.serviceAmount || 0,
            refundAmount: data.refundAmount || 0,
            orderCount: orderFinishNum,
            avgOrderAmount: orderFinishNum > 0 ? orderPayAmount / orderFinishNum : 0
          };
        }
      } catch (e) {
        console.error("加载统计数据失败", e);
      }
    },
    async loadRevenueTrend() {
      try {
        const res = await this.$axios.get("/api/v1/manage/statistics/report", {
          timeType: this.trendTimeType === 7 ? "2" : this.trendTimeType === 30 ? "3" : "4"
        });
        const list = res?.data?.statisticsReportVos || [];
        const xData = list.map(item => {
          let time = item.time || "";
          if (time.includes(" ")) time = time.split(" ")[0];
          if (time.length === 10) time = time.substring(5);
          return time;
        });
        const revenueData = list.map(item => item.orderPayAmount || 0);
        const orderData = list.map(item => item.orderFinishNum || 0);

      const option = {
        tooltip: { trigger: "axis", backgroundColor: "rgba(50, 50, 50, 0.9)", textStyle: { color: "#fff" } },
        legend: { data: ["营收金额", "订单数"], bottom: 0, textStyle: { color: "#666" } },
        grid: { left: "3%", right: "4%", bottom: "15%", top: "10%", containLabel: true },
        xAxis: { type: "category", data: xData, axisLine: { lineStyle: { color: "#ddd" } }, axisLabel: { color: "#666" } },
        yAxis: [
          { type: "value", name: "金额", axisLine: { show: false }, splitLine: { lineStyle: { color: "#f0f0f0" } }, axisLabel: { color: "#666", formatter: v => v >= 10000 ? (v / 10000) + "万" : v } },
          { type: "value", name: "订单", axisLine: { show: false }, splitLine: { show: false }, axisLabel: { color: "#666" } }
        ],
        series: [
          { name: "营收金额", type: "bar", data: revenueData, barWidth: 20, itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: "#67C23A" }, { offset: 1, color: "#85CE61" }]), borderRadius: [4, 4, 0, 0] } },
          { name: "订单数", type: "line", yAxisIndex: 1, smooth: true, data: orderData, itemStyle: { color: "#409EFF" } }
        ]
      };
      const c = this.initChart('revenueTrendChart'); c && c.setOption(option);
      } catch (e) {
        console.error("加载营收趋势失败", e);
      }
    },
    loadRevenueCompose() {
      const option = {
        tooltip: { trigger: "item", formatter: "{b}: ¥{c} ({d}%)" },
        legend: { orient: "vertical", right: "5%", top: "center", textStyle: { color: "#666" } },
        series: [{
          type: "pie",
          radius: ["40%", "65%"],
          center: ["35%", "50%"],
          label: { show: false },
          data: [
            { value: this.stats.ticketRevenue, name: "票款收入", itemStyle: { color: "#409EFF" } },
            { value: this.stats.serviceRevenue, name: "服务费", itemStyle: { color: "#67C23A" } }
          ]
        }]
      };
      const c = this.initChart('revenueComposeChart'); c && c.setOption(option);
    },
    loadLineRevenue() {
      // 线路营收排行暂无专用接口，显示空图表
      const option = {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: "3%", right: "10%", bottom: "3%", top: "3%", containLabel: true },
        xAxis: { type: "value", axisLine: { show: false }, splitLine: { lineStyle: { color: "#f0f0f0" } }, axisLabel: { color: "#666" } },
        yAxis: { type: "category", data: [], axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: "#666" } },
        series: [{
          type: "bar",
          data: [],
          barWidth: 16,
          itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: "#67C23A" }, { offset: 1, color: "#85CE61" }]), borderRadius: [0, 8, 8, 0] }
        }]
      };
      const c = this.initChart('lineRevenueChart'); c && c.setOption(option);
    },
    loadPaymentDist() {
      // 支付方式分布暂无专用接口，显示空图表
      const option = {
        tooltip: { trigger: "item", formatter: "{b}: ¥{c} ({d}%)" },
        legend: { orient: "vertical", right: "5%", top: "center", textStyle: { color: "#666" } },
        series: [{
          type: "pie",
          radius: ["40%", "65%"],
          center: ["35%", "50%"],
          label: { show: false },
          data: []
        }]
      };
      const c = this.initChart('paymentChart'); c && c.setOption(option);
    },
    async loadRevenueList() {
      // 营收明细暂无专用接口，显示空列表
      this.revenueList = [];
    },
    exportData() {
      this.$message.success("导出功能开发中");
    }
  }
};
</script>

<style lang="less" scoped>
.revenue-analysis-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;

  .filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    padding: 16px 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 16px; font-weight: 600; color: #303133; margin-bottom: 16px;
    display: flex; align-items: center;
    i { margin-right: 8px; color: #409EFF; }
  }

  .overview-cards {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 14px;
    margin-bottom: 24px;

    .ov-card {
      background: #fff; border-radius: 10px; padding: 16px;
      display: flex; align-items: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.05);
      transition: all 0.2s ease;
      &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
      .ov-card-icon {
        width: 40px; height: 40px; border-radius: 10px;
        display: flex; align-items: center; justify-content: center;
        margin-right: 12px; flex-shrink: 0;
        i { color: #fff; font-size: 18px; }
      }
      .ov-card-info {
        min-width: 0;
        .ov-card-value { font-size: 16px; font-weight: 700; color: #303133; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ov-card-label { font-size: 12px; color: #909399; margin-top: 2px; }
      }
    }
  }

  .chart-card, .table-card {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    overflow: hidden; min-width: 0;

    .chart-card-header {
      padding: 14px 20px; border-bottom: 1px solid #f0f0f0;
      font-size: 14px; font-weight: 600; color: #303133;
      display: flex; align-items: center; justify-content: space-between;
      i { margin-right: 6px; color: #409EFF; }
    }
    .chart-body { width: 100%; height: 320px; padding: 10px; box-sizing: border-box; }
  }

  .table-card { margin-bottom: 24px; }

  .charts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 20px; }
  .charts-grid-half { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }

  @media (max-width: 1400px) {
    .overview-cards { grid-template-columns: repeat(3, 1fr); }
    .charts-grid { grid-template-columns: repeat(2, 1fr); }
  }
}
</style>
